import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../../lib/api';
import { useSession } from '../../lib/store';
import { Card, Empty, Notice, PageHeading, Spinner } from '../../components/Shell';
import { CopyLink } from '../../components/CopyLink';
import { tenantSiteUrl } from '../../lib/config';
import { BookingStamp, PaymentStamp, Stamp } from '../../components/Stamp';
import { dateRange, money, relativeDays, titleCase } from '../../lib/format';
import type { AvailabilityDay, Booking, ClientProfile } from '../../lib/types';

const NEEDS_ATTENTION: Booking['status'][] = ['requested', 'awaiting_documents', 'awaiting_payment'];

/**
 * The sitter's daily screen: what needs a decision, who is here today, and
 * which nights are filling up. Requests come first, because a booking sitting
 * unanswered is the only thing here that costs money.
 */
export function AdminHome() {
  const session = useSession();
  const tenantId = session.tenant?.tenantId;
  const timezone = session.tenant?.timezone;

  const [bookings, setBookings] = useState<Booking[]>([]);
  const [clients, setClients] = useState<Map<string, ClientProfile>>(new Map());
  const [days, setDays] = useState<AvailabilityDay[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const today = new Date().toISOString().slice(0, 10);

  const load = useCallback(async () => {
    if (!tenantId) return;
    const horizon = new Date();
    horizon.setDate(horizon.getDate() + 60);
    const to = horizon.toISOString().slice(0, 10);

    try {
      const [bookingResult, clientResult, availabilityResult] = await Promise.all([
        api<{ bookings: Booking[] }>(`/tenants/${tenantId}/bookings`),
        api<{ clients: ClientProfile[] }>(`/tenants/${tenantId}/clients`),
        api<{ days: AvailabilityDay[] }>(`/tenants/${tenantId}/availability?from=${today}&to=${to}`),
      ]);

      setBookings(bookingResult.bookings);
      setClients(new Map(clientResult.clients.map((c) => [c.clientId, c])));
      setDays(availabilityResult.days);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The dashboard could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [tenantId, today]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return (
      <div className="mx-auto max-w-5xl px-5 py-16">
        <Spinner label="Loading today" />
      </div>
    );
  }

  const needsDecision = bookings
    .filter((b) => NEEDS_ATTENTION.includes(b.status))
    .sort((a, b) => a.startDate.localeCompare(b.startDate));

  const inCare = bookings.filter(
    (b) => ['confirmed', 'in_progress'].includes(b.status) && b.startDate <= today && b.endDate >= today
  );

  const upcoming = bookings
    .filter((b) => b.status === 'confirmed' && b.startDate > today)
    .sort((a, b) => a.startDate.localeCompare(b.startDate))
    .slice(0, 8);

  const owed = bookings
    .filter((b) => !['cancelled', 'declined'].includes(b.status))
    .reduce((sum, b) => sum + Math.max(0, b.quote.total - b.payment.paidAmount), 0);

  const clientName = (clientId: string) => clients.get(clientId)?.fullName ?? 'Client';

  return (
    <div className="mx-auto max-w-5xl px-5 pb-24 pt-12">
      <PageHeading
        eyebrow={session.tenant?.businessName}
        title="Today"
        actions={
          <>
            <Link className="btn btn-secondary" to="/admin/clients">
              Clients
            </Link>
            <Link className="btn btn-secondary" to="/admin/services">
              Services
            </Link>
          </>
        }
      />

      {error && (
        <div className="mb-8">
          <Notice tone="error">{error}</Notice>
        </div>
      )}

      {/* The address a sitter sends to a client. It lives here because it is
          shared far more often than anything in Settings. */}
      {session.tenant?.slug && (
        <Card className="mb-10 p-5">
          <CopyLink
            url={tenantSiteUrl(session.tenant.slug)}
            label="Your booking page. Send it to anyone who asks, and they can set their pet up and book without a single phone call."
          />
        </Card>
      )}

      {/* --- Numbers that change a decision --------------------------------- */}
      <div className="mb-10 grid gap-4 sm:grid-cols-3">
        <Card className="p-5">
          <p className="field-label">In your care</p>
          <p className="font-display text-4xl font-extrabold">{inCare.length}</p>
        </Card>
        <Card className="p-5">
          <p className="field-label">Waiting on you</p>
          <p className="font-display text-4xl font-extrabold">
            {needsDecision.length}
            {needsDecision.length > 0 && (
              <span className="ml-3 align-middle">
                <Stamp tone="flag" flat>
                  Action
                </Stamp>
              </span>
            )}
          </p>
        </Card>
        <Card className="p-5">
          <p className="field-label">Still owed</p>
          <p className="font-display text-4xl font-extrabold">{money(owed)}</p>
        </Card>
      </div>

      {/* --- Requests -------------------------------------------------------- */}
      <section className="mb-12">
        <h2 className="mb-5 text-2xl">Waiting for your answer</h2>
        {needsDecision.length === 0 ? (
          <Empty title="All caught up" body="Every request has an answer. Nobody is waiting on you." />
        ) : (
          <ul className="space-y-3">
            {needsDecision.map((booking) => (
              <li key={booking.bookingId}>
                <Link
                  to={`/admin/bookings/${booking.bookingId}`}
                  className="card flex flex-wrap items-center justify-between gap-4 p-4 transition-shadow hover:shadow-[0_2px_0_var(--color-stamp)]"
                >
                  <div>
                    <p className="font-display text-lg font-bold">{clientName(booking.clientId)}</p>
                    <p className="typed text-sm text-[color:var(--color-ink-soft)]">
                      {titleCase(booking.serviceCode)} &middot;{' '}
                      {dateRange(booking.startDate, booking.endDate, timezone)} &middot;{' '}
                      {relativeDays(booking.startDate)}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="typed">{money(booking.quote.total)}</span>
                    <BookingStamp status={booking.status} flat />
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* --- In care --------------------------------------------------------- */}
      {/* Every one of these is an animal currently in someone's care and a
          family at an airport wondering how it is going. So the row carries
          the one action that answers them, rather than making her open the
          booking to go looking for it. */}
      {inCare.length > 0 && (
        <section className="mb-12">
          <h2 className="mb-1 text-2xl">Staying with you</h2>
          <p className="mb-5 text-sm text-[color:var(--color-ink-soft)]">
            A photo and a line is the whole job. It takes a minute, and it is the part they tell
            their friends about.
          </p>
          <ul className="space-y-3">
            {inCare.map((booking) => (
              <li
                key={booking.bookingId}
                className="card flex flex-wrap items-center justify-between gap-4 p-4"
              >
                <Link to={`/admin/bookings/${booking.bookingId}`} className="min-w-0">
                  <p className="font-display text-lg font-bold">{clientName(booking.clientId)}</p>
                  <p className="typed text-sm text-[color:var(--color-ink-soft)]">
                    Home {relativeDays(booking.endDate)}
                  </p>
                </Link>
                <div className="flex items-center gap-3">
                  <PaymentStamp status={booking.payment.status} flat />
                  <Link className="btn btn-primary" to={`/admin/bookings/${booking.bookingId}`}>
                    Send an update
                  </Link>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* --- The next two weeks ---------------------------------------------- */}
      <section className="mb-12">
        <h2 className="mb-5 text-2xl">How the next two weeks look</h2>
        <OccupancyStrip days={days.slice(0, 14)} />
      </section>

      {upcoming.length > 0 && (
        <section>
          <h2 className="mb-5 text-2xl">Coming up</h2>
          <ul className="space-y-2">
            {upcoming.map((booking) => (
              <li key={booking.bookingId}>
                <Link
                  to={`/admin/bookings/${booking.bookingId}`}
                  className="flex flex-wrap items-center justify-between gap-4 border-b border-[color:var(--color-rule)] py-3 text-sm hover:text-[color:var(--color-stamp)]"
                >
                  <span>
                    <span className="font-semibold">{clientName(booking.clientId)}</span>
                    <span className="typed ml-3 text-[color:var(--color-ink-soft)]">
                      {dateRange(booking.startDate, booking.endDate, timezone)}
                    </span>
                  </span>
                  <PaymentStamp status={booking.payment.status} flat />
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

/**
 * A fortnight of nights as a row of columns. Height shows how full each night
 * is, so a week that is filling up is visible without reading a single number.
 */
function OccupancyStrip({ days }: { days: AvailabilityDay[] }) {
  if (days.length === 0) {
    return <p className="text-sm text-[color:var(--color-ink-soft)]">No dates loaded.</p>;
  }

  return (
    <ol className="flex gap-1.5 overflow-x-auto pb-2">
      {days.map((day) => {
        const capacity = Math.max(1, day.boarding.capacity);
        const fill = Math.min(1, day.boarding.booked / capacity);
        const full = day.boarding.available === 0;

        return (
          <li key={day.date} className="min-w-11 flex-1">
            <div
              className="flex h-24 flex-col justify-end border border-[color:var(--color-rule)] bg-white"
              title={`${day.date}: ${day.boarding.booked} of ${day.boarding.capacity} booked`}
            >
              <div
                className={
                  day.blocked
                    ? 'bg-[color:var(--color-ink-faint)]'
                    : full
                      ? 'bg-[color:var(--color-flag)]'
                      : 'bg-[color:var(--color-field)]'
                }
                style={{ height: `${day.blocked ? 100 : fill * 100}%` }}
              />
            </div>
            <p className="mt-1 text-center font-mono text-[0.6rem] text-[color:var(--color-ink-faint)]">
              {day.date.slice(8)}
            </p>
            {day.isHoliday && (
              <p className="text-center font-mono text-[0.55rem] text-[color:var(--color-stamp)]">HOL</p>
            )}
          </li>
        );
      })}
    </ol>
  );
}
